import React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import DefaultLayout from "/zeit/512c25b6/node_modules/gatsby-theme-carbon/src/templates/Default.js";
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const PageDescription = makeShortcode("PageDescription");
const AnchorLinks = makeShortcode("AnchorLinks");
const AnchorLink = makeShortcode("AnchorLink");
const Row = makeShortcode("Row");
const Column = makeShortcode("Column");
const ResourceCard = makeShortcode("ResourceCard");
const MdxIcon = makeShortcode("MdxIcon");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <PageDescription mdxType="PageDescription">
      <p>{`The transition from v9 to v10 is primarily visual. The IBM color palette was updated between versions and a 16 column grid was introduced.`}</p>
    </PageDescription>
    <AnchorLinks mdxType="AnchorLinks">
      <AnchorLink mdxType="AnchorLink">IBM Design Language</AnchorLink>
      <AnchorLink mdxType="AnchorLink">Design kit</AnchorLink>
      <AnchorLink mdxType="AnchorLink">Elements</AnchorLink>
      <AnchorLink mdxType="AnchorLink">Design migration strategy</AnchorLink>
    </AnchorLinks>
    <h2>{`IBM Design Language`}</h2>
    <p>{`Carbon v10 delivers the `}<a parentName="p" {...{
        "href": "https://www.ibm.com/design/language/"
      }}>{`IBM Design Language`}</a>{` to product teams. It represents a complete visual refresh of the system, delivering the ethos of IBM Design. Carbon v10 is a modern, open-source framework for building digital products and experiences, with tooling and guidance on color, layout, typography, iconography, motion, patterns, and content. `}</p>
    <h2>{`Design kit`}</h2>
    <p>{`All components, colors, icons, typography, motion, and grid elements in the kit align with the IBM Design Language.`}</p>
    <h3>{`What changed`}</h3>
    <ul>
      <li parentName="ul">{`Sketch kit delivered with Sketch Cloud`}</li>
      <li parentName="ul">{`New color tokens`}</li>
      <li parentName="ul">{`Updated text styles`}</li>
      <li parentName="ul">{`Updated spacing`}</li>
      <li parentName="ul">{`Icon updates`}</li>
      <li parentName="ul">{`Updated layer styles`}</li>
    </ul>
    <h4>{`Sketch libraries`}</h4>
    <p>{`Designers can subscribe to the design kit library via Sketch Cloud, and updates to the contents of the kit will be pushed to subscribers as they become available.`}</p>
    <h4>{`Icons, pictograms, and the full IBM color palette`}</h4>
    <p>{`To access the IBM Design Language icons, pictograms, and colors, subscribe to the `}<a parentName="p" {...{
        "href": "/resources#color-grid-and-icons"
      }}>{`IBM Design Language libraries`}</a>{`.`}</p>
    <h4>{`Migrating to v10`}</h4>
    <p>{`Upgrading to the v10 kit will not automatically change any files created in previous versions of the kit. Swapping v9 symbols for v10 symbols is a manual process.`}</p>
    <p>{`Although libraries from multiple versions of Carbon can exist in Sketch at the same time, you should avoid using v10 symbols in an app designed with v9.`}</p>
    <h3>{`Setting up the kit`}</h3>
    <p>{`Head over to the `}<a parentName="p" {...{
        "href": "/get-started/design/sketch#get-the-kit"
      }}>{`Get started guide`}</a>{` to set up the new Sketch libraries.`}</p>
    <h2>{`Elements`}</h2>
    <h3>{`Themes and color`}</h3>
    <p>{`Carbon offers four color themes. See the `}<a parentName="p" {...{
        "href": "/guidelines/color/overview#themes"
      }}>{`theming guidelines`}</a>{` to learn how to use the themes, and visit the color migration guide to learn more about color tokens added in v10.`}</p>
    <Row mdxType="Row">
      <Column colLg={4} colMd={4} noGutterSm mdxType="Column">
        <ResourceCard subTitle="Migrating color" href="https://github.com/carbon-design-system/carbon/blob/master/docs/migration/10.x-color.md" mdxType="ResourceCard">
          <MdxIcon name="github" mdxType="MdxIcon" />
  </ResourceCard>
      </Column>
    </Row>
    <h3>{`Icons`}</h3>
    <p>{`The `}<a parentName="p" {...{
        "href": "/guidelines/icons/library"
      }}>{`icon library`}</a>{` was updated and expanded with v10. The full library of icons and pictograms is available via the IBM Design Language icon `}<a parentName="p" {...{
        "href": "/guidelines/icons/usage#resources"
      }}>{`Sketch library`}</a>{`.`}</p>
    <p>{`Any v9 icons should be reviewed against the new icons before publishing. Wherever possible, replace old icons with an updated version.`}</p>
    <Row mdxType="Row">
      <Column colLg={4} colMd={4} noGutterSm mdxType="Column">
        <ResourceCard subTitle="Icon migration" href="https://github.com/carbon-design-system/carbon/blob/master/docs/migration/10.x-icons.md" mdxType="ResourceCard">
          <MdxIcon name="github" mdxType="MdxIcon" />
  </ResourceCard>
      </Column>
    </Row>
    <h3>{`Typography`}</h3>
    <p>{`The typeface for Carbon remains `}<a parentName="p" {...{
        "href": "/guidelines/typography/overview/#typeface:-ibm-plex"
      }}>{`IBM Plex`}</a>{`. The `}<a parentName="p" {...{
        "href": "/guidelines/typography/overview#type-tokens-and-sets"
      }}>{`type token`}</a>{` architecture was redesigned for better clarity and flexibility, moving from a basic type scale model to a more robust token-based architecture. Work with your development team to discuss how this new architecture might affect your team’s workflow.`}</p>
    <Row mdxType="Row">
      <Column colLg={4} colMd={4} noGutterSm mdxType="Column">
        <ResourceCard subTitle="Type migration" href="https://github.com/carbon-design-system/carbon/blob/master/docs/migration/10.x-type.md" mdxType="ResourceCard">
          <MdxIcon name="github" mdxType="MdxIcon" />
  </ResourceCard>
      </Column>
    </Row>
    <h3>{`Motion`}</h3>
    <p>{`Carbon v10 adheres to IBM Design Language `}<a parentName="p" {...{
        "href": "/guidelines/motion/basics"
      }}>{`motion standards`}</a>{`, designed to bring unity and cohesion to all motion in a user interface. All interactions that include motion should abide by these standards.`}</p>
    <h3>{`Layout, grid, and spacing`}</h3>
    <p>{`Carbon v10 uses the 16 column `}<a parentName="p" {...{
        "href": "/guidelines/2x-grid/basics"
      }}>{`2x Grid`}</a>{`. Carbon v10 will default to the 12-column grid with an option to switch to the 16-column grid. Designers starting on new layouts using v10 should use the 16-column grid.`}</p>
    <p>{`Layout and spacing token names were updated in v10 but the values remain the same.`}</p>
    <Row mdxType="Row">
      <Column colLg={4} colMd={4} noGutterSm mdxType="Column">
        <ResourceCard subTitle="Layout migration" href="https://github.com/carbon-design-system/carbon/blob/master/docs/migration/10.x-layout.md#migrating" mdxType="ResourceCard">
          <MdxIcon name="github" mdxType="MdxIcon" />
  </ResourceCard>
      </Column>
    </Row>
    <h2>{`Design migration strategy`}</h2>
    <p>{`The fastest approach to v10 migration starts with the front-end developer. Developers should update the product’s front end code to v10 and conduct a visual review with team designers. The transition should not cause any major breaks in the UI but there will likely be layout and color issues. Product teams using v9 components in their code should see a mostly seamless code transition.`}</p>
    <p>{`Throughout visual review and iteration process, ask:`}</p>
    <ul>
      <li parentName="ul">{`Are you using type tokens?`}</li>
      <li parentName="ul">{`Are you using the color tokens correctly?`}</li>
      <li parentName="ul">{`Is the spacing between components correct?`}</li>
      <li parentName="ul">{`Do animations in the UI match the new motion standards?`}</li>
      <li parentName="ul">{`Does the general layout still work, and does it express the IBM Design Language accurately and effectively?`}</li>
    </ul>
    <p>{`Following this review, work any development and design issues into your team’s planning workflow.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      